import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Settings, Eye, RotateCcw, Loader2, Save, CheckCircle, AlertTriangle, Star } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import DOMPurify from 'dompurify';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { SenderStatus } from '@/components/SenderStatus';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NOTIF_TYPES = [
  { id: 'new_booking', label: 'Nuova Prenotazione', color: 'bg-green-500/20 text-green-400' },
  { id: 'confirmation', label: 'Confermata', color: 'bg-accent/20 text-accent' },
  { id: 'cancelled', label: 'Annullata', color: 'bg-red-500/20 text-red-500' },
  { id: 'modified', label: 'Modifica Generale', color: 'bg-yellow-500/20 text-yellow-500' },
  { id: 'modified_date', label: 'Modifica Data', color: 'bg-amber-500/20 text-amber-400' },
  { id: 'modified_time', label: 'Modifica Orario', color: 'bg-orange-500/20 text-orange-400' },
  { id: 'reminder_24h', label: 'Promemoria 24h', color: 'bg-blue-500/20 text-blue-500' },
  { id: 'reminder_2h', label: 'Promemoria 2h', color: 'bg-purple-500/20 text-purple-500' },
  { id: 'review_request', label: 'Recensione Google', color: 'bg-amber-500/20 text-amber-300' },
];

const VARIABLES_HELP = '{nome}, {servizio}, {data}, {ora}, {operatore}, {salone}, {modifiche}';

const AdminNotificationSettingsSection = () => {
  const { getAuthHeaders } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewType, setPreviewType] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [activeTemplateTab, setActiveTemplateTab] = useState('new_booking');

  const [salonPhone, setSalonPhone] = useState('');
  const [salonEmail, setSalonEmail] = useState('');
  const [salonAddress, setSalonAddress] = useState('');
  const [templates, setTemplates] = useState({});
  const [reviewUrl, setReviewUrl] = useState('');
  const [reviewEnabled, setReviewEnabled] = useState(true);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/notification-settings`, { headers: getAuthHeaders() });
      const s = res.data;
      setSettings(s);
      setSalonPhone(s.salon_phone || '');
      setSalonEmail(s.salon_email || '');
      setSalonAddress(s.salon_address || '');
      setTemplates(s.templates || {});
      setReviewUrl(s.google_review_url || '');
      setReviewEnabled(s.review_email_enabled !== false);
    } catch (error) {
      toast.error('Errore caricamento impostazioni');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const payload = {
        salon_phone: salonPhone,
        salon_email: salonEmail,
        salon_address: salonAddress,
        templates,
        google_review_url: reviewUrl,
        review_email_enabled: reviewEnabled,
      };
      await axios.put(`${API}/admin/notification-settings`, payload, { headers: getAuthHeaders() });
      toast.success('Impostazioni salvate!');
      fetchSettings();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async (type) => {
    setPreviewType(type);
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const res = await axios.get(`${API}/admin/notification-preview/${type}`, { headers: getAuthHeaders() });
      setPreviewHtml(res.data.html);
    } catch (error) {
      toast.error('Errore anteprima');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleResetTemplates = async () => {
    try {
      const res = await axios.post(`${API}/admin/notification-settings/reset-templates`, {}, { headers: getAuthHeaders() });
      setTemplates(res.data.templates);
      toast.success('Template ripristinati ai valori predefiniti');
    } catch (error) {
      toast.error('Errore ripristino');
    }
  };

  const updateTemplate = (type, field, value) => {
    setTemplates(prev => ({
      ...prev,
      [type]: { ...prev[type], [field]: value }
    }));
  };

  if (loading) return <div className="flex items-center justify-center h-48"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;

  return (
    <div className="space-y-8" data-testid="notification-settings-section">
      <h2 className="text-2xl sm:text-3xl font-bold">Impostazioni Notifiche</h2>

      {/* Brevo Status */}
      <Card className="card-elegant" data-testid="brevo-status-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <Settings className="h-5 w-5 text-accent" />
            <h3 className="text-lg font-bold">Stato Email (Brevo)</h3>
            {settings?.brevo_configured ? (
              <Badge className="bg-green-500/20 text-green-400 text-xs" data-testid="brevo-status-badge">
                <CheckCircle className="h-3 w-3 mr-1 inline" /> Configurato
              </Badge>
            ) : (
              <Badge className="bg-yellow-500/20 text-yellow-500 text-xs" data-testid="brevo-status-badge">
                <AlertTriangle className="h-3 w-3 mr-1 inline" /> API Key mancante
              </Badge>
            )}
          </div>
          {!settings?.brevo_configured && (
            <p className="text-xs text-muted-foreground mt-2">
              Configura BREVO_API_KEY, BREVO_SENDER_EMAIL e BREVO_SENDER_NAME nel file .env del backend per attivare l'invio email.
            </p>
          )}
          {settings?.brevo_configured && <SenderStatus getAuthHeaders={getAuthHeaders} />}
        </CardContent>
      </Card>

      {/* Salon Contact Info for Email Footer */}
      <Card className="card-elegant" data-testid="salon-contact-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Settings className="h-5 w-5 text-accent" />
            <h3 className="text-lg font-bold">Contatti Salone (Footer Email)</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Telefono</Label>
              <Input value={salonPhone} onChange={(e) => setSalonPhone(e.target.value)} data-testid="salon-phone-input" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={salonEmail} onChange={(e) => setSalonEmail(e.target.value)} data-testid="salon-email-input" />
            </div>
            <div>
              <Label>Indirizzo</Label>
              <Input value={salonAddress} onChange={(e) => setSalonAddress(e.target.value)} placeholder="Via Roma 1, Milano" data-testid="salon-address-input" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Google Review Email */}
      <Card className="card-elegant" data-testid="review-email-card">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-lg font-bold flex items-center gap-2"><Star className="h-5 w-5 text-accent" /> Email Recensione Google</h3>
            <Switch checked={reviewEnabled} onCheckedChange={setReviewEnabled} data-testid="review-email-enabled" />
          </div>
          <p className="text-sm text-muted-foreground">Inviata automaticamente circa 3 ore dopo la fine di ogni appuntamento confermato, con il pulsante "Lascia una recensione su Google". Il testo lo modifichi in "Template Email → Recensione Google".</p>
          <div>
            <Label>Link alla tua pagina Google</Label>
            <Input value={reviewUrl} onChange={(e) => setReviewUrl(e.target.value)} placeholder="https://maps.app.goo.gl/..." className="text-base" data-testid="review-url-input" />
          </div>
          {reviewUrl && <a href={reviewUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-accent underline" data-testid="review-url-test-link">Prova il link</a>}
        </CardContent>
      </Card>

      {/* Message Templates */}
      <Card className="card-elegant" data-testid="templates-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold">Template Email</h3>
            <Button variant="outline" size="sm" onClick={handleResetTemplates} data-testid="reset-templates-btn">
              <RotateCcw className="h-4 w-4 mr-1" /> Ripristina Default
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Variabili disponibili: <code className="bg-secondary px-1 py-0.5 rounded">{VARIABLES_HELP}</code>
          </p>

          <Tabs value={activeTemplateTab} onValueChange={setActiveTemplateTab}>
            <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0 mb-4">
              {NOTIF_TYPES.map(t => (
                <TabsTrigger key={t.id} value={t.id} className="data-[state=active]:bg-accent data-[state=active]:text-accent-foreground text-xs px-3 py-1.5">
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {NOTIF_TYPES.map(t => {
              const tpl = templates[t.id] || {};
              return (
                <TabsContent key={t.id} value={t.id} className="space-y-4 mt-0">
                  <div>
                    <Label>Oggetto Email</Label>
                    <Input value={tpl.email_subject || ''} onChange={(e) => updateTemplate(t.id, 'email_subject', e.target.value)}
                      data-testid={`tpl-${t.id}-subject`} />
                  </div>
                  <div>
                    <Label>Testo Email</Label>
                    <Textarea value={tpl.email_body || ''} onChange={(e) => updateTemplate(t.id, 'email_body', e.target.value)}
                      rows={3} data-testid={`tpl-${t.id}-email-body`} />
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handlePreview(t.id)} data-testid={`preview-${t.id}-btn`}>
                    <Eye className="h-4 w-4 mr-1" /> Anteprima Email
                  </Button>
                </TabsContent>
              );
            })}
          </Tabs>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSaveSettings} disabled={saving} className="bg-accent text-accent-foreground hover:bg-accent/90 px-8" data-testid="save-settings-btn">
          {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Salvataggio...</> : <><Save className="h-4 w-4 mr-2" /> Salva Impostazioni</>}
        </Button>
      </div>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle>Anteprima: {NOTIF_TYPES.find(t => t.id === previewType)?.label || previewType}</DialogTitle>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>
          ) : (
            <div className="border border-border rounded-lg overflow-hidden" data-testid="email-preview-frame">
              <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml) }} style={{ background: '#1a1a1a', padding: '16px' }} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminNotificationSettingsSection;
